import React from 'react';
import { Formik, Form } from 'formik';
import { Box, Link, Typography, useMediaQuery } from '@mui/material';
import { ResetCodeData } from 'Types';
import ResetCodeForm, { resetCodeFormSchema } from 'Components/Forms/ResetCodeForm';
import { ValidateForm } from 'Utils/Forms';
import { isPhoneNumber } from 'Components/Forms/SignUpForm';
import GoBackButton from 'Components/GoBackButton';
import { MUSEFLOW_BLUE, MUSEFLOW_CYAN, MUSEFLOW_OFF_WHITE_1 } from 'Components/Colors';
import {
  CODE_RESENT_MESSAGE,
  EMAIL_CODE_RESENT_MESSAGE,
  useAuthWrapperContext,
} from 'Contexts/AuthWrapperContext';
import { usePressedState } from 'Utils/usePressedState';
import { SIGN_IN_COMPONENT } from 'Types/AuthTypes';
import { CircularProgress } from '@mui/material';

// MUS-607: the Back link sits in the card's top-left corner, where the
// centred heading also starts once the card is narrow or the heading is long,
// so the heading was printed over it. Where the card is tall enough, the
// content starts below the link; a phone in landscape (480px high or less)
// has the link beside the heading and needs no room for it.
export const ROOM_FOR_BACK_LINK_QUERY = '(min-height: 481px)';
export const ROOM_FOR_BACK_LINK_PX = 56;
// MUS-607: in a short window (a phone in landscape) the card's logo hangs
// over the card's top edge and the heading started under it. There the
// content starts 20px lower, and the space around the SMS hint and above the
// code field gives up more than that, so Submit ends up higher than before,
// not lower. The resend link keeps 16px clear of the code field (it was 4px
// with the hint margins left at 20px), so the two are not tapped by mistake.
export const ROOM_FOR_LOGO_SHORT_PX = 20;
export const SPACER_MARGIN_PX = 20;
export const SPACER_MARGIN_SHORT_PX = 8;
export const HINT_MARGIN_PX = 20;
export const HINT_MARGIN_SHORT_PX = 14;

type VerificationCodeProps = {
  onSubmit?: (code: string) => void;
  backFn?: () => void;
};

const VerificationCode: React.FC<VerificationCodeProps> = (props) => {
  const { onSubmit, backFn } = props;

  const {
    pendingEmailOrPhone,
    resetEmailOrPhone,
    resetCountryAcronym: _resetCountryAcronym,
    pendingCountryAcronym,
    signUpCodeResent,
    errors,
    processPending,
    resendSignUpUsername,
    setConfirmationCode,
    confirmSignUpUsername,
    setSignInComponent,
    resetErrors,
  } = useAuthWrapperContext();
  const resendPress = usePressedState();
  const tallEnoughForRoom = useMediaQuery(ROOM_FOR_BACK_LINK_QUERY);
  const roomAbove = tallEnoughForRoom ? ROOM_FOR_BACK_LINK_PX : ROOM_FOR_LOGO_SHORT_PX;
  const roomForBackLink = backFn ? `${roomAbove}px` : 0;
  const shortWithBackLink = !!backFn && !tallEnoughForRoom;
  const spacerVerticalMargin = shortWithBackLink ? SPACER_MARGIN_SHORT_PX : SPACER_MARGIN_PX;
  const hintVerticalMargin = shortWithBackLink ? HINT_MARGIN_SHORT_PX : HINT_MARGIN_PX;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        minHeight: '300px',
        paddingTop: roomForBackLink,
      }}
    >
      {backFn && (
        <GoBackButton
          sx={{
            position: 'absolute',
            top: 30,
            left: 30,
            color: MUSEFLOW_OFF_WHITE_1,
          }}
          onClick={() => backFn()}
        >
          Back
        </GoBackButton>
      )}
      {pendingEmailOrPhone && (
        <>
          <h3
            style={{
              marginBottom: '20px',
              color: 'rgb(255,255,255,1.0)',
            }}
          >
            {isPhoneNumber(pendingEmailOrPhone) ? 'Verify Your Phone Number' : 'Verify Your Email'}
          </h3>
          <Box
            data-verification-hint=""
            sx={{
              margin: `${hintVerticalMargin}px ${HINT_MARGIN_PX}px`,
              color: MUSEFLOW_CYAN,
            }}
          >
            {isPhoneNumber(pendingEmailOrPhone)
              ? 'Check your SMS messages for a code.'
              : 'Check your email for a code.'}
          </Box>
          <Link
            sx={{
              textAlign: 'center' as const,
              cursor: 'pointer',
              color: MUSEFLOW_BLUE,
              ...resendPress.pressedSx,
            }}
            {...resendPress.pressHandlers}
            onClick={() => {
              resendSignUpUsername(pendingEmailOrPhone || '');
            }}
          >
            Didn't get a Code? Click here to resend.
          </Link>
          {signUpCodeResent && (
            <Typography role="status" color="#00C2FF" sx={{ marginTop: '10px' }}>
              {isPhoneNumber(pendingEmailOrPhone) ? CODE_RESENT_MESSAGE : EMAIL_CODE_RESENT_MESSAGE}
            </Typography>
          )}
        </>
      )}
      {!processPending && (
        <>
          <Box
            data-verification-spacer=""
            sx={{
              maxWidth: '400px',
              margin: `${spacerVerticalMargin}px ${SPACER_MARGIN_PX}px`,
            }}
          ></Box>
          <Formik
            initialValues={{
              code: '',
              unknown: '',
            }}
            validate={(values: ResetCodeData) => {
              return ValidateForm(resetCodeFormSchema, values);
            }}
            onSubmit={async (values) => {
              if (setConfirmationCode) setConfirmationCode(values.code);
              if (pendingEmailOrPhone && isPhoneNumber(pendingEmailOrPhone)) {
                await confirmSignUpUsername({
                  phoneNumber: pendingEmailOrPhone,
                  confirmationCode: values.code,
                  countryAcronym: pendingCountryAcronym || 'US',
                });
              } else if (resetEmailOrPhone) {
                if (setSignInComponent) setSignInComponent(SIGN_IN_COMPONENT.RESET_PASSWORD);
              }
              if (onSubmit) {
                onSubmit(values.code);
              }
            }}
            validateOnChange={false}
          >
            {(formikProps) => (
              <Form
                onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                  e.preventDefault();
                  if (resetErrors) resetErrors();
                  formikProps.handleSubmit(e);
                }}
              >
                <Box sx={{ width: '350px', display: 'flex', flexDirection: 'column' }}>
                  <ResetCodeForm {...formikProps} errors={{ ...errors, ...formikProps.errors }} />
                </Box>
              </Form>
            )}
          </Formik>
        </>
      )}
      {processPending && <CircularProgress />}
    </Box>
  );
};

export default VerificationCode;
